// LiveDataTab — Bvndle's live numbers surface inside Knowledge.
//
// Renders every bound query in the Metabase catalog as a chart card on
// mount so the ManCo walks in to live numbers instead of an empty state.
// Below the dashboard, the VFD ecosystem conversion funnel (stage-by-
// stage) renders if any stages have SQL bound. QueryAsk stays at the
// bottom for ad-hoc exploration during the meeting. The long-tail partner
// ecosystem panel sits underneath that.
//
// File was `FunnelPage.tsx` pre-Phase-4. Renamed when the IA collapsed the
// Funnel/Documents/Ask trio into one Knowledge surface — the page is now
// the "Live data" tab, never a standalone route. Old /funnel URL redirects
// here via App.tsx for bookmark compatibility.
//
// Default data source: the Metabase instance baked into the build (see
// src/config/metabase-defaults.ts). Auto-registered on first launch by
// App.tsx — no setup step.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Chart } from "../../components/Chart";
import { QueryAsk } from "../../components/QueryAsk";
import { fetchFunnelSnapshot } from "../../lib/data-sources";
import {
  LONG_TAIL_PARTNERS,
  PARTNER_ECOSYSTEM,
  type PartnerRow,
} from "../../lib/partner-ecosystem";
import {
  listCatalogQueries,
  runCatalogQuery,
  type CatalogEntry,
  type QueryRunResult,
} from "../../lib/query-catalog";
import type { FunnelSnapshotWire, FunnelStageResult } from "../../lib/types";

interface DashboardCard {
  entry: CatalogEntry;
  result: QueryRunResult | null;
  loading: boolean;
  error: string | null;
}

/**
 * @param hideHeader  When rendered inside IntelligencePage as a tab, the
 * parent owns the page header — refresh + data-source actions move to an
 * inline surface toolbar instead.
 */
export function LiveDataTab({ hideHeader = true }: { hideHeader?: boolean } = {}) {
  const [cards, setCards] = useState<DashboardCard[]>([]);
  const [snap, setSnap] = useState<FunnelSnapshotWire | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);

  const load = async (force = false) => {
    setRefreshing(true);
    setBootError(null);
    try {
      const [catalog, funnelSnap] = await Promise.all([
        listCatalogQueries(),
        fetchFunnelSnapshot(force).catch(() => null),
      ]);
      // Only show queries that have SQL bound (sample-only entries clutter
      // the dashboard). Order: live first, then by catalog order.
      const bound = catalog.queries.filter((q) => !!q.sql);
      const initial: DashboardCard[] = bound.map((e) => ({
        entry: e,
        result: null,
        loading: true,
        error: null,
      }));
      setCards(initial);
      setSnap(funnelSnap);

      // Resolve charts in parallel (Metabase handles concurrency fine).
      bound.forEach(async (entry, idx) => {
        try {
          const result = await runCatalogQuery(entry.id);
          setCards((prev) => {
            const next = prev.slice();
            next[idx] = { entry, result, loading: false, error: null };
            return next;
          });
        } catch (e) {
          setCards((prev) => {
            const next = prev.slice();
            next[idx] = { entry, result: null, loading: false, error: String(e) };
            return next;
          });
        }
      });
    } catch (e) {
      setBootError(String(e));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void load(false);
  }, []);

  const stageRows = useMemo(() => {
    if (!snap) return [];
    const byId = new Map<string, FunnelStageResult>();
    snap.stages.forEach((s) => byId.set(s.id, s));
    const top = snap.stages[0];
    return snap.stages.map((s) => {
      const denomId = s.denominator_id ?? top?.id;
      const denom = denomId ? byId.get(denomId)?.count ?? null : null;
      const pct =
        s.count != null && denom != null && denom > 0 ? (s.count / denom) * 100 : null;
      const pctOfTop =
        s.count != null && top?.count != null && top.count > 0
          ? (s.count / top.count) * 100
          : null;
      return { stage: s, denom, pct, pctOfTop };
    });
  }, [snap]);

  const someStagesBound = snap?.stages.some((s) => s.status === "ok") ?? false;
  const liveCount = cards.filter((c) => c.result?.status === "live").length;

  const navigate = useNavigate();

  return (
    <>
      {!hideHeader && (
        <FunnelHead
          refreshing={refreshing}
          onRefresh={() => load(true)}
          live={liveCount}
          total={cards.length}
        />
      )}

      {hideHeader && (
        <div style={surfaceToolbar}>
          <div style={surfaceMeta}>
            DATA · KEY INDICATORS
            {cards.length > 0 ? ` · ${liveCount}/${cards.length} LIVE` : ""}
          </div>
          <div style={surfaceActions}>
            <button
              className="btn btn--ghost"
              type="button"
              onClick={() => navigate("/settings/data-sources")}
            >
              Data sources
            </button>
            <button
              className="btn btn--primary"
              type="button"
              disabled={refreshing}
              onClick={() => load(true)}
            >
              {refreshing ? "Refreshing…" : "Refresh now"}
            </button>
          </div>
        </div>
      )}

      {bootError && (
        <div className="fn-callout" style={{ marginBottom: 18 }}>
          {bootError}
        </div>
      )}

      {/* Ad-hoc query · sticky at the top so it stays visible while scrolling
          the chart grid and stage funnel below. */}
      <div style={stickyAskWrap}>
        <QueryAsk />
      </div>

      {/* Key indicators dashboard */}
      <div style={chartGrid}>
        {cards.map((c) => (
          <ChartCard key={c.entry.id} card={c} />
        ))}
      </div>

      {/* Stage funnel (only if any stages are bound) */}
      {snap && someStagesBound && (
        <div className="card" style={{ padding: 24, marginTop: 18 }}>
          <div style={sectionHead}>VFD ECOSYSTEM CONVERSION · STAGE FUNNEL</div>
          <div className="fn">
            {stageRows.map((r, i) => (
              <FunnelRow key={r.stage.id} row={r} rowIndex={i} totalRows={stageRows.length} />
            ))}
          </div>
          <div className="fn-status-row" style={{ marginTop: 22, marginBottom: 0 }}>
            <span className={snap.cache_status === "cached" ? "dot cached" : "dot"} />
            <span>{snap.cache_status === "cached" ? "CACHED" : "FRESH"}</span>
            <span className="ts">pulled {snap.pulled_at}</span>
          </div>
        </div>
      )}

      {/* Partner ecosystem · supplementary view for the long-tail merchants
          intentionally kept off the SYM/D1 KPI cards. ManCo signal-vs-noise
          lens: anchors stay on the cards, the tail lives here. */}
      <PartnerEcosystemPanel />

    </>
  );
}

function ChartCard({ card }: { card: DashboardCard }) {
  const { entry, result, loading, error } = card;
  return (
    <div className="card" style={chartCard}>
      <div style={cardHead}>
        <div>
          <div style={cardTitle}>{entry.title || entry.id}</div>
          {entry.subtitle && <div style={cardSubtitle}>{entry.subtitle}</div>}
        </div>
        {result && (
          <span style={liveTag(result.status)}>
            {result.status === "live" ? "LIVE" : result.status === "sample" ? "SAMPLE" : "ERROR"}
          </span>
        )}
      </div>
      <div style={chartBody}>
        {loading && <div style={loadingState}>Loading…</div>}
        {error && <div style={errorState}>{error}</div>}
        {result && !error && (
          <Chart spec={entry.chart} result={result} />
        )}
      </div>
    </div>
  );
}

function FunnelHead({
  refreshing,
  onRefresh,
  live,
  total,
}: {
  refreshing: boolean;
  onRefresh: () => void;
  live: number;
  total: number;
}) {
  const navigate = useNavigate();
  return (
    <div className="page-head">
      <div>
        <div className="page-head__meta">
          DATA · KEY INDICATORS{total > 0 ? ` · ${live}/${total} LIVE` : ""}
        </div>
        <h1>Funnel</h1>
      </div>
      <div className="page-head__actions">
        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => navigate("/settings/data-sources")}
        >
          Data sources
        </button>
        <button className="btn btn--primary" type="button" disabled={refreshing} onClick={onRefresh}>
          {refreshing ? "Refreshing…" : "Refresh now"}
        </button>
      </div>
    </div>
  );
}

interface RowInfo {
  stage: FunnelStageResult;
  denom: number | null;
  pct: number | null;
  pctOfTop: number | null;
}

function FunnelRow({
  row,
  rowIndex,
}: {
  row: RowInfo;
  rowIndex: number;
  totalRows: number;
}) {
  const { stage, pct, pctOfTop } = row;
  const widthPct = pctOfTop ?? (rowIndex === 0 ? 100 : 0);
  const classes = [`fn-row`, `r${rowIndex}`];
  if (stage.is_cliff) classes.push("cliff");
  if (stage.status === "error") classes.push("err");

  return (
    <div className={classes.join(" ")}>
      <div className="fn-track">
        <div className="fn-bar" style={{ width: `${Math.max(2, widthPct)}%` }} />
        <div className="fn-overlay">
          <span className="label">{stage.label}</span>
          <span className="value">
            <span className="count">{fmt(stage.count)}</span>
            <span className="pct">{pct != null ? `${pct.toFixed(1)}%` : "—"}</span>
          </span>
        </div>
      </div>
      <div className="fn-meta">
        <span className={`status ${stage.status === "error" ? "err" : stage.is_cliff ? "warn" : ""}`}>
          {stageStatusLabel(stage)}
        </span>
        {stage.description}
      </div>
    </div>
  );
}

function stageStatusLabel(s: FunnelStageResult): string {
  if (s.status === "error") return "Error";
  if (s.status === "unconfigured") return "Unconfigured";
  if (s.is_cliff) return "Cliff";
  return "Measured";
}

function fmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("en-US");
}

// — styles —

const surfaceToolbar: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: "0.5px solid var(--edge)",
};
const surfaceMeta: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  fontWeight: 700,
  color: "var(--ink-faint)",
  textTransform: "uppercase",
};
const surfaceActions: React.CSSProperties = {
  display: "flex",
  gap: 10,
};

const stickyAskWrap: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 5,
  marginBottom: 18,
  background: "var(--bg-0)",
  paddingBottom: 6,
  // soft fade so charts behind don't bleed through the sticky region
  boxShadow: "0 8px 14px -8px rgba(0,0,0,0.6)",
};

const chartGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 16,
};
const chartCard: React.CSSProperties = {
  padding: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const cardHead: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
  padding: "14px 18px 8px",
  borderBottom: "1px solid var(--edge)",
};
const cardTitle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: "var(--ink)",
  letterSpacing: "0.02em",
};
const cardSubtitle: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-faint)",
  marginTop: 3,
  lineHeight: 1.4,
};
const chartBody: React.CSSProperties = {
  padding: "10px 8px 14px",
  minHeight: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const loadingState: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.18em",
  color: "var(--ink-faint)",
};
const errorState: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--coral)",
  padding: 20,
  textAlign: "center",
  lineHeight: 1.5,
};
function liveTag(status: string): React.CSSProperties {
  const color =
    status === "live" ? "var(--purple)" : status === "sample" ? "var(--amber)" : "var(--coral)";
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    letterSpacing: "0.14em",
    fontWeight: 700,
    color,
    border: `1px solid ${color}`,
    borderRadius: 5,
    padding: "2px 6px",
    flexShrink: 0,
  };
}
const sectionHead: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.2em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
  marginBottom: 12,
};

// ===========================================================================
// Partner Ecosystem · Long-Tail Panel
//
// Strategic split: the top-2 anchors (Yard 41.6%, GARA-MART 22.9%) + named
// anchor clients (BvndleGO etc.) surface on the SYM/D1 KPI cards. Everything
// else lives here as supplementary detail so the ManCo can scan the breadth
// without it noising up the strategic surface. Ownership is coded per partner
// (see src/lib/partner-ecosystem.ts).
// ===========================================================================

function PartnerEcosystemPanel() {
  const { totals, tiers, categories, flags } = PARTNER_ECOSYSTEM;
  const tailItems = LONG_TAIL_PARTNERS.reduce((sum, p) => sum + p.items, 0);
  const tailShare = tailItems / totals.listedItems;

  return (
    <div className="card" style={{ padding: 24, marginTop: 18 }}>
      <div style={pxHead}>
        <div>
          <div style={sectionHead}>PARTNER ECOSYSTEM · LONG-TAIL MAP</div>
          <div style={pxSubtitle}>
            ManCo lens · the {LONG_TAIL_PARTNERS.length} partners outside the strategic anchors.
            Anchors (Yard, GARA-MART, BvndleGO) surface on the KPI cards;
            everything below lives here.
          </div>
        </div>
        <div style={pxStamp}>
          <span style={pxStampMono}>SNAPSHOT</span>
          <span style={pxStampDate}>12 May 2026</span>
        </div>
      </div>

      {/* Headline totals strip */}
      <div style={pxKpiRow}>
        <PxStat label="Active partners"    value={String(totals.activePartners)} sub="All tiers" />
        <PxStat label="Listed items"       value={String(totals.listedItems)}    sub="Approved and live" />
        <PxStat label="Categories"         value={String(totals.activeCategories)} sub="All tiers" />
        <PxStat label="Long-tail items"    value={String(tailItems)}             sub={`${(tailShare * 100).toFixed(1)}% of inventory`} />
        <PxStat label="Cheapest entry"     value={String(totals.cheapestCoins)}  sub={`BC · ${totals.cheapestPartner}`} />
        <PxStat label="Peak month"         value={String(totals.peakInventoryMonth.items)} sub={`items · ${totals.peakInventoryMonth.label}`} />
      </div>

      <div style={pxGrid}>
        {/* Tier breakdown · left column */}
        <div>
          <div style={pxColHead}>Pricing tiers</div>
          <div style={pxPanel}>
            <table style={pxTable}>
              <thead>
                <tr>
                  <th style={pxTh}>Tier</th>
                  <th style={pxTh}>Coin range</th>
                  <th style={{ ...pxTh, textAlign: "right" }}>Partners</th>
                  <th style={{ ...pxTh, textAlign: "right" }}>Items</th>
                  <th style={{ ...pxTh, textAlign: "right" }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => (
                  <tr key={t.tier}>
                    <td style={pxTd}><strong>{t.tier}</strong></td>
                    <td style={pxTdMuted}>{t.coinRange}</td>
                    <td style={{ ...pxTd, textAlign: "right" }}>{t.partners}</td>
                    <td style={{ ...pxTd, textAlign: "right", fontWeight: t.flag ? 700 : 400 }}>{t.items}</td>
                    <td style={{ ...pxTdMuted, textAlign: "right" }}>
                      {(t.share * 100).toFixed(1)}%
                      {t.flag && <span style={pxTagWarn}>{t.flag}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Category distribution · middle column */}
        <div>
          <div style={pxColHead}>Category distribution</div>
          <div style={pxPanel}>
            {categories.map((c) => {
              const topItems = categories[0]?.items ?? 1;
              const widthPct = (c.items / topItems) * 100;
              return (
                <div key={c.name} style={pxBarRow}>
                  <div style={pxBarLabel}>
                    {c.name}
                    {!c.priority && <span style={pxTagTail}>tail</span>}
                  </div>
                  <div style={pxBarTrack}>
                    <div
                      style={{
                        ...pxBarFill,
                        width: `${Math.max(2, widthPct)}%`,
                        background: c.priority ? "var(--purple, #534AB7)" : "var(--ink-faint, #888)",
                      }}
                    />
                  </div>
                  <div style={pxBarValue}>{c.items}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Concentration flags · right column */}
        <div>
          <div style={pxColHead}>Concentration flags</div>
          <div style={pxPanel}>
            {flags.map((f) => (
              <div key={f.label} style={pxFlagRow}>
                <span style={pxFlagLabel}>{f.label}</span>
                <span style={pxFlagValue}>
                  {f.value}
                  {f.tone === "warn" && <span style={pxTagWarn}>watch</span>}
                  {f.tone === "ok" && <span style={pxTagOk}>ok</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The long tail itself · ranked list with owner chips */}
      <div style={{ marginTop: 22 }}>
        <div style={pxColHead}>Long-tail partners · ranked · owner-coded</div>
        <div style={pxLongTailGrid}>
          {LONG_TAIL_PARTNERS.map((p) => (
            <PartnerCard key={p.name} partner={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PxStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div style={pxStat}>
      <div style={pxStatLabel}>{label}</div>
      <div style={pxStatValue}>{value}</div>
      <div style={pxStatSub}>{sub}</div>
    </div>
  );
}

function PartnerCard({ partner }: { partner: PartnerRow }) {
  const maxItems = LONG_TAIL_PARTNERS[0]?.items ?? 1;
  const widthPct = (partner.items / maxItems) * 100;
  return (
    <div style={partnerCard}>
      <div style={partnerCardHead}>
        <span style={partnerRank}>#{partner.rank}</span>
        <span style={partnerName} title={partner.name}>{partner.name}</span>
      </div>
      <div style={partnerMeta}>
        <span style={partnerCategory}>{partner.category}</span>
        <span style={partnerOwner} title={`Owner: ${partner.owner}`}>{partner.owner}</span>
      </div>
      <div style={partnerBar}>
        <div style={{ ...partnerBarFill, width: `${Math.max(4, widthPct)}%` }} />
      </div>
      <div style={partnerStats}>
        <span>{partner.items} items</span>
        <span>{(partner.share * 100).toFixed(1)}%</span>
      </div>
    </div>
  );
}

// — Partner panel styles —
const pxHead: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  marginBottom: 16,
};
const pxSubtitle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-faint)",
  marginTop: 4,
  lineHeight: 1.5,
  maxWidth: 640,
};
const pxStamp: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 2,
  flexShrink: 0,
};
const pxStampMono: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.18em",
  color: "var(--ink-faint)",
  fontWeight: 700,
};
const pxStampDate: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink)",
  fontWeight: 600,
};
const pxKpiRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: 8,
  marginBottom: 20,
};
const pxStat: React.CSSProperties = {
  background: "var(--bg-1, rgba(255,255,255,0.03))",
  border: "1px solid var(--edge)",
  borderRadius: 6,
  padding: "10px 12px",
};
const pxStatLabel: React.CSSProperties = {
  fontSize: 10,
  color: "var(--ink-faint)",
  marginBottom: 4,
  lineHeight: 1.3,
};
const pxStatValue: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  color: "var(--ink)",
  lineHeight: 1,
};
const pxStatSub: React.CSSProperties = {
  fontSize: 10,
  color: "var(--ink-faint)",
  marginTop: 4,
};
const pxGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 12,
};
const pxColHead: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.14em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
  marginBottom: 8,
};
const pxPanel: React.CSSProperties = {
  background: "var(--bg-1, rgba(255,255,255,0.03))",
  border: "1px solid var(--edge)",
  borderRadius: 6,
  padding: "12px 14px",
};
const pxTable: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 11.5,
};
const pxTh: React.CSSProperties = {
  fontSize: 9,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--ink-faint)",
  fontWeight: 600,
  padding: "0 0 6px",
  borderBottom: "1px solid var(--edge)",
  textAlign: "left",
};
const pxTd: React.CSSProperties = {
  padding: "5px 0",
  fontSize: 11.5,
  color: "var(--ink)",
  borderBottom: "1px solid var(--edge)",
};
const pxTdMuted: React.CSSProperties = {
  ...pxTd,
  color: "var(--ink-faint)",
};
const pxBarRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 7,
};
const pxBarLabel: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-soft, var(--ink))",
  width: 130,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
};
const pxBarTrack: React.CSSProperties = {
  flex: 1,
  background: "var(--edge)",
  borderRadius: 2,
  height: 8,
};
const pxBarFill: React.CSSProperties = {
  height: 8,
  borderRadius: 2,
};
const pxBarValue: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink)",
  fontWeight: 600,
  width: 36,
  textAlign: "right",
  flexShrink: 0,
};
const pxFlagRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "5px 0",
  borderBottom: "1px solid var(--edge)",
  fontSize: 11.5,
  gap: 8,
};
const pxFlagLabel: React.CSSProperties = {
  color: "var(--ink-faint)",
  flexShrink: 0,
};
const pxFlagValue: React.CSSProperties = {
  fontWeight: 500,
  color: "var(--ink)",
  textAlign: "right",
  display: "flex",
  alignItems: "center",
  gap: 6,
  justifyContent: "flex-end",
};
const pxTagWarn: React.CSSProperties = {
  display: "inline-block",
  fontSize: 9,
  padding: "2px 6px",
  borderRadius: 3,
  fontWeight: 600,
  marginLeft: 6,
  background: "rgba(163, 45, 45, 0.18)",
  color: "var(--coral, #d96b6b)",
};
const pxTagOk: React.CSSProperties = {
  display: "inline-block",
  fontSize: 9,
  padding: "2px 6px",
  borderRadius: 3,
  fontWeight: 600,
  marginLeft: 6,
  background: "rgba(15, 110, 86, 0.18)",
  color: "var(--mint, #5ec98c)",
};
const pxTagTail: React.CSSProperties = {
  display: "inline-block",
  fontSize: 9,
  padding: "1px 5px",
  borderRadius: 3,
  fontWeight: 500,
  marginLeft: 6,
  background: "var(--edge)",
  color: "var(--ink-faint)",
};
const pxLongTailGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: 10,
};
const partnerCard: React.CSSProperties = {
  background: "var(--bg-1, rgba(255,255,255,0.03))",
  border: "1px solid var(--edge)",
  borderRadius: 6,
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
};
const partnerCardHead: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
};
const partnerRank: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  color: "var(--ink-faint)",
  fontWeight: 700,
  flexShrink: 0,
};
const partnerName: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--ink)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const partnerMeta: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 6,
};
const partnerCategory: React.CSSProperties = {
  fontSize: 10,
  color: "var(--ink-faint)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const partnerOwner: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: "var(--purple, #8d83ff)",
  background: "rgba(83, 74, 183, 0.14)",
  padding: "2px 6px",
  borderRadius: 3,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "60%",
};
const partnerBar: React.CSSProperties = {
  background: "var(--edge)",
  borderRadius: 2,
  height: 4,
  overflow: "hidden",
  marginTop: 2,
};
const partnerBarFill: React.CSSProperties = {
  height: 4,
  borderRadius: 2,
  background: "var(--purple, #534AB7)",
};
const partnerStats: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: 10,
  color: "var(--ink-faint)",
};
